import { useContext, useMemo } from 'react';
import { Chip } from '@mui/material';
import { IntlShape, useIntl } from 'react-intl';
import { Tile } from 'components/Layout/PageContainer';

import { PriceChange } from 'pages/trading/model';
import { MetricsChart } from 'components/MetricsChart';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { ColumnChartRenderer } from 'components/chart/renderer/ColumnChartRenderer';
import { TenantConfigContext, TenantConfigUtil } from 'context/TenantConfigContext';
import { tbPalette } from 'styles/colors';

interface AggregatedData {
  label: string;
  oldQty: number;
  promoQty: number;
  newQty: number;
  otherQty: number;
  gmv: number;
}

interface HistoryMapEntry {
  timestamp: number;
  oldQty: number;
  promoQty: number;
  newQty: number;
  otherQty: number;
  gmv: number;
}

const equals = (a: number, b: number) => Math.abs(a - b) < 0.001;

const formatDateLabel = (timestamp: number, intl: IntlShape): string => {
  const d = new Date(timestamp);
  return intl.formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
};

const aggregateDataByDate = (data: PriceChange[], intl: IntlShape, isPromotion: boolean): AggregatedData[] => {
  const historyMap = new Map<string, HistoryMapEntry>();
  let minTime = Infinity;
  let maxTime = -Infinity;

  if (!data || data.length === 0) return [];

  data.forEach(item => {
    const oldPriceVal = Number(item.fromPrice?.price || 0);
    const promoPriceVal = isPromotion ? Number(item.promotionPrice?.price || 0) : null;
    const newPriceVal = Number(item.toPrice?.price || 0);

    let promoStartTime = Infinity;
    let promoEndTime = -Infinity;

    if (Array.isArray(item.history)) {
      item.history.forEach(histGroup => {
        const histPrice = Number(histGroup.price || 0);
        const isTriggerPrice = isPromotion ? equals(histPrice, promoPriceVal!) : equals(histPrice, newPriceVal);

        if (isTriggerPrice && Array.isArray(histGroup.entries)) {
          histGroup.entries.forEach(entry => {
            if (entry.date) {
              const time = new Date(entry.date).getTime();
              if (time < promoStartTime) promoStartTime = time;
              if (time > promoEndTime) promoEndTime = time;
            }
          });
        }
      });
    }

    const midPromoTime = (promoStartTime + promoEndTime) / 2;

    if (Array.isArray(item.history)) {
      item.history.forEach(histGroup => {
        const histPrice = Number(histGroup.price || 0);

        const matchesOld = equals(histPrice, oldPriceVal);
        const matchesPromo = isPromotion && promoPriceVal !== null && equals(histPrice, promoPriceVal);
        const matchesNew = equals(histPrice, newPriceVal);

        if (Array.isArray(histGroup.entries)) {
          histGroup.entries.forEach(entry => {
            if (!entry.date) return;
            const dateVal = new Date(entry.date).getTime();
            if (isNaN(dateVal)) return;

            if (dateVal < minTime) minTime = dateVal;
            if (dateVal > maxTime) maxTime = dateVal;

            const key = new Date(dateVal).toISOString().split('T')[0];
            const current = historyMap.get(key) || {
              timestamp: dateVal,
              oldQty: 0,
              promoQty: 0,
              newQty: 0,
              otherQty: 0,
              gmv: 0,
            };

            const qty = Number(entry.ordQty || 0);
            const gmv = qty * histPrice;

            if (matchesPromo) {
              current.promoQty += qty;
            } else if (matchesOld && matchesNew) {
              if (dateVal < midPromoTime) {
                current.oldQty += qty;
              } else {
                current.newQty += qty;
              }
            } else if (matchesOld) {
              current.oldQty += qty;
            } else if (matchesNew) {
              current.newQty += qty;
            } else {
              current.otherQty += qty;
            }

            current.gmv += gmv;
            historyMap.set(key, current);
          });
        }
      });
    }
  });

  if (minTime === Infinity) return [];

  const result: AggregatedData[] = [];
  let currentTime = minTime;
  const oneDay = 24 * 60 * 60 * 1000;

  while (currentTime <= maxTime) {
    const key = new Date(currentTime).toISOString().split('T')[0];
    const label = formatDateLabel(currentTime, intl);

    if (historyMap.has(key)) {
      const entry = historyMap.get(key)!;
      result.push({
        label,
        oldQty: entry.oldQty,
        promoQty: entry.promoQty,
        newQty: entry.newQty,
        otherQty: entry.otherQty,
        gmv: entry.gmv,
      });
    } else {
      result.push({
        label,
        oldQty: 0,
        promoQty: 0,
        newQty: 0,
        otherQty: 0,
        gmv: 0,
      });
    }
    currentTime += oneDay;
  }
  return result;
};

// --- Component Props ---

interface StyleDetailChartProps {
  data: PriceChange[];
  selectedArticleId: string | null;
  onClearFilter: () => void;
  isPromotion?: boolean;
}

// --- Component Definition ---

export function StyleDetailChart({
  data,
  selectedArticleId,
  onClearFilter,
  isPromotion = false,
}: StyleDetailChartProps) {
  const intl = useIntl();

  const { currencyCode } = useContext(TenantConfigContext);
  const currencySymbol = TenantConfigUtil.getCurrencySymbol(currencyCode);

  const timelineData = useMemo(() => {
    return aggregateDataByDate(data, intl, isPromotion);
  }, [data, intl, isPromotion]);

  const priceInfo = useMemo(() => {
    if (!data || data.length === 0) return { old: undefined, promo: undefined, new: undefined };
    return {
      old: data[0].fromPrice?.price,
      promo: isPromotion ? data[0].promotionPrice?.price : undefined,
      new: data[0].toPrice?.price,
    };
  }, [data, isPromotion]);

  const otherPriceRange = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    let hasOther = false;

    data.forEach(item => {
      const oldPriceVal = Number(item.fromPrice?.price || 0);
      const promoPriceVal = isPromotion ? Number(item.promotionPrice?.price || 0) : null;
      const newPriceVal = Number(item.toPrice?.price || 0);

      item.history?.forEach(histGroup => {
        const histPrice = Number(histGroup.price || 0);
        const isOld = equals(histPrice, oldPriceVal);
        const isPromo = isPromotion && promoPriceVal !== null && equals(histPrice, promoPriceVal);
        const isNew = equals(histPrice, newPriceVal);

        if (!isOld && !isPromo && !isNew) {
          if (histGroup.entries && histGroup.entries.length > 0) {
            if (histPrice < min) min = histPrice;
            if (histPrice > max) max = histPrice;
            hasOther = true;
          }
        }
      });
    });

    return hasOther ? { min, max } : null;
  }, [data, isPromotion]);

  // --- CHART: QUANTITY (STACKED BARS) + GMV (LINE) ---
  const qtyChartRenderer = useMemo(() => {
    if (!timelineData.length) return null;

    const config = new BarChartConfig();
    config.limitXAxis = false;
    config.value = {
      unit: MeasureUnitCode.QUANTITY,
      label: intl.formatMessage({ id: 'insights.measure.omsOrdQty.name' }),
    };
    config.breakdownMode = 'stacked';
    config.additionalAxis = true;
    config.secondaryAsLine = true;
    config.highlightLineColor = tbPalette.surface.grey;

    const maxGmv = Math.max(...timelineData.map(d => d.gmv));
    const maxGmvAxis = maxGmv > 0 ? maxGmv * 1.1 : 100;

    config.additionalValue = {
      code: 'gmv',
      label: 'Ordered GMV',
      unit: MeasureUnitCode.CURRENCY,
      max: maxGmvAxis,
    };

    const hasOtherData = timelineData.some(d => d.otherQty > 0);

    const totalSeries = new ChartDataArray(
      ChartDataType.primary,
      'Total',
      timelineData.map(d => new ChartData(d.label, d.oldQty + d.promoQty + d.newQty + d.otherQty))
    );

    const formatLabel = (id: string, defaultMessage: string, price?: number) => {
      const text = intl.formatMessage({ id, defaultMessage });
      if (price !== undefined) {
        const formattedPrice = intl.formatNumber(price, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return `${text} (${formattedPrice} ${currencySymbol})`;
      }
      return `${text} (Qty)`;
    };

    // Series 1: Old Price
    const oldQtySeries = new ChartDataArray(
      ChartDataType.breakdown,
      formatLabel('trading.style.price.chart.legend.old_price', 'Old Price', priceInfo.old),
      timelineData.map(d => new ChartData(d.label, d.oldQty))
    );
    oldQtySeries.color = '#D1D1D1';

    const seriesList = [totalSeries, oldQtySeries];

    // Series 2 (optional): Promo Price
    if (isPromotion) {
      const promoQtySeries = new ChartDataArray(
        ChartDataType.breakdown,
        formatLabel('trading.style.price.chart.legend.promo_price', 'Promo Price', priceInfo.promo),
        timelineData.map(d => new ChartData(d.label, d.promoQty))
      );
      promoQtySeries.color = '#111827';
      seriesList.push(promoQtySeries);
    }

    // Series 3: New Price
    const newQtySeries = new ChartDataArray(
      ChartDataType.breakdown,
      formatLabel('trading.style.price.chart.legend.new_price', 'New Price', priceInfo.new),
      timelineData.map(d => new ChartData(d.label, d.newQty))
    );
    newQtySeries.color = '#0B9AAB';
    seriesList.push(newQtySeries);

    if (hasOtherData) {
      // Series 4: Other Price
      let otherLabel = intl.formatMessage({
        id: 'trading.style.price.chart.legend.other',
      });

      if (otherPriceRange) {
        const minFmt = intl.formatNumber(otherPriceRange.min, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (equals(otherPriceRange.min, otherPriceRange.max)) {
          otherLabel = `Other Price (${minFmt} ${currencySymbol})`;
        } else {
          const maxFmt = intl.formatNumber(otherPriceRange.max, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          otherLabel = `Other Prices (${minFmt} ${currencySymbol} - ${maxFmt}) ${currencySymbol}`;
        }
      }

      const otherQtySeries = new ChartDataArray(
        ChartDataType.breakdown,
        otherLabel,
        timelineData.map(d => new ChartData(d.label, d.otherQty))
      );
      otherQtySeries.color = '#FFCA9B';
      seriesList.push(otherQtySeries);
    }

    // Series 5: GMV Line (Secondary)
    const gmvSeries = new ChartDataArray(
      ChartDataType.secondary,
      intl.formatMessage({
        id: 'trading.style.price.chart.tooltip.gmv',
      }),
      timelineData.map(d => {
        const p = new ChartData(d.label, d.gmv);
        p.color = '#ED6861';
        return p;
      })
    );
    gmvSeries.color = '#ED6861';

    seriesList.push(gmvSeries);

    config.data = seriesList;

    return new ColumnChartRenderer(config);
  }, [timelineData, intl, currencySymbol, priceInfo, isPromotion, otherPriceRange]);

  const overTime = intl.formatMessage({
    id: 'general.title.over_time',
  });

  return (
    <Tile
      titleId="trading.style.price.chart.title"
      additionalTitle={overTime}
      rightHandTitleComponent={
        selectedArticleId && (
          <Chip
            label={`${intl.formatMessage({ id: 'common.filtered_by' })}: ${selectedArticleId}`}
            onDelete={onClearFilter}
            color="primary"
            variant="outlined"
            size="small"
          />
        )
      }
    >
      <MetricsChart renderer={qtyChartRenderer} height="h-[350px]" testId="chart-qty" />
    </Tile>
  );
}
